import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, X, Move, Minimize2, Maximize2 } from 'lucide-react';

interface FloatingTimerProps {
  isVisible: boolean;
  onClose: () => void;
}

// 播放清脆的下课电铃声
const playAlarmSound = () => {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: AudioContext }).webkitAudioContext)();
    const startTime = ctx.currentTime;
    
    // 下课电铃声效果 - 清脆响亮
    // 使用方波产生电铃音色，配合高频谐波
    const frequencies = [880, 1100, 880, 1100]; // 交替频率产生电铃效果
    
    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // 使用方波产生电铃音色
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(freq, startTime + index * 0.15);
      
      // 清脆的音量包络
      const noteDuration = 0.4;
      gainNode.gain.setValueAtTime(0, startTime + index * 0.15);
      gainNode.gain.linearRampToValueAtTime(0.4, startTime + index * 0.15 + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + index * 0.15 + noteDuration);
      
      oscillator.start(startTime + index * 0.15);
      oscillator.stop(startTime + index * 0.15 + noteDuration);
    });
    
    // 添加高频谐波增强清脆感
    const harmonics = [1760, 2200, 1760, 2200];
    harmonics.forEach((freq, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, startTime + index * 0.15 + 0.05);
      
      const noteDuration = 0.3;
      gainNode.gain.setValueAtTime(0, startTime + index * 0.15 + 0.05);
      gainNode.gain.linearRampToValueAtTime(0.2, startTime + index * 0.15 + 0.07);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + index * 0.15 + 0.05 + noteDuration);
      
      oscillator.start(startTime + index * 0.15 + 0.05);
      oscillator.stop(startTime + index * 0.15 + 0.05 + noteDuration);
    });
    
  } catch {
    // Audio not supported
  }
};

export default function FloatingTimer({ isVisible, onClose }: FloatingTimerProps) {
  const [duration, setDuration] = useState(60);
  const [remaining, setRemaining] = useState(60);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 200, y: window.innerHeight - 280 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const playFinishSound = useCallback(() => {
    playAlarmSound();
  }, []);

  useEffect(() => {
    if (isRunning && !isPaused) {
      timerRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            playFinishSound();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, isPaused, playFinishSound]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    if (remaining > 0) {
      setIsRunning(true);
      setIsPaused(false);
    }
  };

  const pauseTimer = () => {
    setIsPaused(!isPaused);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setRemaining(duration);
  };

  const adjustTime = (delta: number) => {
    if (isRunning) return;
    const newDuration = Math.max(10, Math.min(600, duration + delta));
    setDuration(newDuration);
    setRemaining(newDuration);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.timer-control-btn')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - (isMinimized ? 120 : 180), e.clientX - dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - (isMinimized ? 50 : 120), e.clientY - dragOffset.y));
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, isMinimized]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('.timer-control-btn')) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragOffset({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const newX = Math.max(0, Math.min(window.innerWidth - (isMinimized ? 120 : 180), touch.clientX - dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - (isMinimized ? 50 : 120), touch.clientY - dragOffset.y));
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, isMinimized]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleTouchMove, handleTouchEnd]);

  if (!isVisible) return null;

  const progress = duration > 0 ? ((duration - remaining) / duration) * 100 : 0;
  const isLowTime = remaining <= 10 && remaining > 0 && isRunning;
  const isCritical = remaining <= 5 && remaining > 0 && isRunning;

  if (isMinimized) {
    return (
      <div
        ref={containerRef}
        className="fixed cursor-move select-none"
        style={{ left: position.x, top: position.y, zIndex: 9999 }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className={`avalon-card p-2 flex items-center gap-2 ${isCritical ? 'ring-2 ring-red-400 animate-pulse' : isLowTime ? 'ring-2 ring-avalon-gold' : ''}`}>
          <div className={`text-lg font-bold font-mono ${isCritical ? 'text-red-400' : 'text-avalon-gold'}`}>
            {formatTime(remaining)}
          </div>
          <button
            onClick={() => setIsMinimized(false)}
            className="timer-control-btn w-6 h-6 rounded bg-avalon-purple flex items-center justify-center text-white hover:bg-avalon-purple-light"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed cursor-move select-none"
      style={{ left: position.x, top: position.y, zIndex: 9999 }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className={`avalon-card p-4 w-[160px] ${isCritical ? 'ring-2 ring-red-400' : isLowTime ? 'ring-2 ring-avalon-gold' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1 text-avalon-text text-xs">
            <Move className="w-3 h-3" />
            <span>拖拽移动</span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="timer-control-btn w-6 h-6 rounded bg-avalon-purple flex items-center justify-center text-white hover:bg-avalon-purple-light"
            >
              <Minimize2 className="w-3 h-3" />
            </button>
            <button
              onClick={onClose}
              className="timer-control-btn w-6 h-6 rounded bg-red-500/50 flex items-center justify-center text-white hover:bg-red-500"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Time Display */}
        <div className="text-center mb-3">
          <div className={`text-3xl font-bold font-mono ${isCritical ? 'text-red-400' : 'text-avalon-gold'}`}>
            {formatTime(remaining)}
          </div>
          <div className="w-full h-1 bg-avalon-purple rounded-full mt-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ${isCritical ? 'bg-red-400' : 'bg-avalon-gold'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        {!isRunning ? (
          <div className="flex gap-2">
            <button
              onClick={startTimer}
              disabled={remaining === 0}
              className="timer-control-btn flex-1 avalon-btn py-1 text-xs flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <Play className="w-3 h-3" />
              开始
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={pauseTimer}
              className="timer-control-btn flex-1 avalon-btn avalon-btn-secondary py-1 text-xs flex items-center justify-center gap-1"
            >
              {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              {isPaused ? '继续' : '暂停'}
            </button>
          </div>
        )}

        {/* Time Adjustment */}
        {!isRunning && (
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => adjustTime(-10)}
              className="timer-control-btn w-8 h-8 rounded bg-avalon-purple flex items-center justify-center text-white text-sm hover:bg-avalon-purple-light"
            >
              -
            </button>
            <span className="text-avalon-text text-xs">{duration}秒</span>
            <button
              onClick={() => adjustTime(10)}
              className="timer-control-btn w-8 h-8 rounded bg-avalon-purple flex items-center justify-center text-white text-sm hover:bg-avalon-purple-light"
            >
              +
            </button>
          </div>
        )}

        {/* Reset */}
        {(isRunning || remaining !== duration) && (
          <button
            onClick={resetTimer}
            className="timer-control-btn w-full mt-2 avalon-btn avalon-btn-secondary py-1 text-xs flex items-center justify-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            重置
          </button>
        )}
      </div>
    </div>
  );
}
